/**
 * Token encoding and decoding for Cashero.
 *
 * A "token" is a portable collection of proofs that can be transferred
 * between wallets. Tokens are encoded as `casheroA` + base64(JSON).
 */

import { bytesToHex, randomBytes } from '@noble/hashes/utils';
import { utf8ToBytes } from '@noble/hashes/utils';
import type { Proof } from './proof';

/** The prefix for Cashero token strings */
export const TOKEN_PREFIX = 'casheroA';

/** The unit identifier for piconero tokens */
export const TOKEN_UNIT = 'piconero';

/**
 * A transferable Cashero token.
 */
export interface CasheroToken {
  /** The mint's npub (hex pubkey) that issued these proofs */
  mint: string;
  /** The unit (always 'piconero') */
  unit: string;
  /** The proofs included in this token */
  proofs: Proof[];
  /** Optional memo / description */
  memo?: string;
}

/** Internal serialization format */
interface TokenJSON {
  mint: string;
  unit: string;
  proofs: Proof[];
  memo?: string;
}

/**
 * Generate a random request ID (UUID v4 style).
 */
export function generateRequestId(): string {
  const bytes = randomBytes(16);
  // Set version 4 bits
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  return bytesToHex(bytes);
}

/**
 * Encode a token into a portable string format.
 *
 * Format: `casheroA` + base64(JSON.stringify({mint, unit, proofs, memo}))
 */
export function encodeToken(token: CasheroToken): string {
  const json: TokenJSON = {
    mint: token.mint,
    unit: token.unit ?? TOKEN_UNIT,
    proofs: token.proofs.map(p => ({
      secret: p.secret,
      C: p.C,
      id: p.id,
      amount: p.amount,
    })),
  };
  if (token.memo) json.memo = token.memo;

  const jsonStr = JSON.stringify(json);
  const base64 = btoa(jsonStr);
  return `${TOKEN_PREFIX}${base64}`;
}

/**
 * Decode a token from a portable string format.
 *
 * @param tokenString - The encoded token (casheroA...)
 * @returns The decoded token
 * @throws If the token string is invalid
 */
export function decodeToken(tokenString: string): CasheroToken {
  if (!tokenString.startsWith(TOKEN_PREFIX)) {
    throw new Error(`Invalid token prefix: expected ${TOKEN_PREFIX}`);
  }

  const base64 = tokenString.slice(TOKEN_PREFIX.length);
  let jsonStr: string;
  try {
    jsonStr = atob(base64);
  } catch {
    throw new Error('Invalid base64 encoding in token');
  }

  let json: TokenJSON;
  try {
    json = JSON.parse(jsonStr);
  } catch {
    throw new Error('Invalid JSON in token');
  }

  if (!json.mint || typeof json.mint !== 'string') {
    throw new Error('Token missing mint field');
  }
  if (!Array.isArray(json.proofs) || json.proofs.length === 0) {
    throw new Error('Token missing proofs array');
  }

  return {
    mint: json.mint,
    unit: json.unit ?? TOKEN_UNIT,
    proofs: json.proofs.map(p => ({
      secret: p.secret,
      C: p.C,
      id: p.id,
      amount: p.amount,
    })),
    memo: json.memo,
  };
}

/**
 * Get token metadata (mint, amount, unit, memo) without decoding full proofs.
 */
export function getTokenMetadata(tokenString: string): {
  mint: string;
  amount: number;
  unit: string;
  memo?: string;
} {
  const token = decodeToken(tokenString);
  const total = token.proofs.reduce((sum, p) => sum + p.amount, 0);
  return {
    mint: token.mint,
    amount: total,
    unit: token.unit,
    memo: token.memo,
  };
}
